<template>
  <v-card flat
          tile>
    <v-toolbar color="secondary"
               dark
               flat>
      <template v-slot:extension>
        <v-row>
          <v-col>
            <v-text-field clearable
                          flat
                          hide-details
                          prepend-icon="mdi-magnify"
                          solo
                          v-bind:label="$t('search')"
                          v-model="search" />
          </v-col>
          <v-col>
            <v-autocomplete clearable
                            flat
                            hide-details
                            multiple
                            prepend-icon="mdi-domain"
                            solo
                            v-bind:items="tenants | items({ text: 'title', value: 'id' })"
                            v-bind:label="$t('tenant')"
                            v-model="tenantFilter" />
          </v-col>
          <v-col>
            <v-autocomplete clearable
                            flat
                            hide-details
                            multiple
                            prepend-icon="mdi-account-tie"
                            solo
                            v-bind:items="departments | items({ text: 'title', value: 'id' })"
                            v-bind:label="$t('department')"
                            v-model="departmentFilter" />
          </v-col>
          <v-col>
            <v-autocomplete clearable
                            flat
                            hide-details
                            multiple
                            prepend-icon="mdi-tag"
                            solo
                            v-bind:items="categories | items({ text: 'text', value: 'id' })"
                            v-bind:label="$t('category')"
                            v-model="categoryFilter" />
          </v-col>
        </v-row>
      </template>
      <v-toolbar-title>{{ $t('trainees') }}</v-toolbar-title>
    </v-toolbar>
    <v-data-table v-bind:footer-props="{ itemsPerPageOptions: [ 5, 10, 30, 50, 100, -1 ] }"
                  v-bind:headers="headers"
                  v-bind:items="filteredTrainees"
                  v-bind:items-per-page="50"
                  v-bind:loading="loading"
                  v-bind:options="{ sortBy: [ 'familyName', 'givenName' ] }"
                  v-on:click:row="goToItem">
      <template v-slot:[`item.departmentId`]="{ item }">
        {{ departmentTitle(item.departmentId) }}
      </template>
      <template v-slot:[`item.tenantId`]="{ item }">
        {{ learningTenantTitle(item.tenantId) }}
      </template>
      <template v-for="category in categories"
                 v-slot:[`item.categoryId_${category.id}`]="{ item }">
        <v-checkbox
    
                    v-bind:input-value="getCategoryValue(item, category.id)"
                    v-on:change="setCategoryValue(item, category.id, !getCategoryValue(item, category.id))"
                    @click.stop=""
                    hide-details="always"
                    class="ma-0 pa-0" />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  export default {
    computed: {
      filteredTrainees() {
        let filteredTrainees = this.trainees

        if (this.search != null && this.search.length !== 0) {
          filteredTrainees = filteredTrainees.filter(f => `${f.number} ${f.givenName} ${f.familyName}`.toLowerCase().includes(this.search.toLowerCase()))
        }

        if (this.tenantFilter != null && this.tenantFilter.length !== 0) {
          filteredTrainees = filteredTrainees.filter(f => this.tenantFilter.includes(f.tenantId))
        }

        if (this.departmentFilter != null && this.departmentFilter.length !== 0) {
          filteredTrainees = filteredTrainees.filter(f => this.departmentFilter.includes(f.departmentId))
        }

        if (this.categoryFilter != null && this.categoryFilter.length !== 0) {
          filteredTrainees = filteredTrainees.filter(f => f.categories != null && f.categories.some(c => this.categoryFilter.includes(c)))
        }

        return filteredTrainees
      },
      sortedCategories() {
        return this.categories.slice().sort((a, b) => a.sort - b.sort)
      },
      categoryHeaders() {
        return this.sortedCategories.map(category => ({
          text: category.text,
          value: `categoryId_${category.id}`,
        }))
      },
      headers() {
        return [
          {
            text: this.$t('number'),
            value: 'number'
          },
          {
            text: this.$t('givenName'),
            value: 'givenName'
          },
          {
            text: this.$t('familyName'),
            value: 'familyName'
          },
          {
            text: this.$t('tenant'),
            value: 'tenantId'
          },
          {
            text: this.$t('department'),
            value: 'departmentId'
          },
          ...this.categoryHeaders
        ]
      },
      ...mapGetters({
        departmentTitle: 'common/departmentTitle',
        tenants: 'learning/learningTenants',
        tenantTitle: 'local/tenantTitle',
        learningTenantTitle: 'learning/learningTenantTitle',
        categories: 'learning/categories',
        departments: 'common/departments',
        categories: 'learning/categories'
      })
    },
    data: () => ({
      search: null,
      tenantFilter: null,
      departmentFilter: null,
      categoryFilter: null
    }),
    methods: {
      getCategoryValue(item, categoryId) {
        return Array.isArray(item.categories) && item.categories.includes(categoryId)
      },
      async setCategoryValue(item, categoryId, value) {
        if (!Array.isArray(item.categories)) {
          item.categories = []
        }
        const idx = item.categories.indexOf(categoryId)
        if (value && idx === -1) {
          item.categories.push(categoryId)
        } else if (!value && idx !== -1) {
          item.categories.splice(idx, 1)
        }

        await this.doUpdateTrainee({ trainee: item })
      },
      goToItem(item) {
        this.$router.push({ name: this.itemRouteName, params: { id: item.id }})
      },
      ...mapActions({
        doUpdateTrainee: 'learning/updateTrainee'
      })
    },
    name: 'TraineeTableCard',
    props: {
      trainees: Array,
      itemRouteName: String,
      loading: Boolean
    }
  }
</script>

<i18n>
  {
    "de": {
      "department": "Abteilung",
      "familyName": "Nachname",
      "givenName": "Vorname",
      "number": "Nummer",
      "search": "Suche",
      "tenant": "Mandant",
      "trainees": "Trainees",
      "departmentId": "Abteilung",
      "category": "Kategorie"
    },
    "en": {
      "department": "Department",
      "familyName": "Family name",
      "givenName": "Given name",
      "number": "Number",
      "search": "Search",
      "tenant": "Tenant",
      "trainees": "Trainees",
      "departmentId": "Department",
      "category": "Category"
    }
  }
</i18n>